import {
    HandThumbDownIcon as HandThumbDownOutlineIcon,
    HandThumbUpIcon as HandThumbUpOutlineIcon,
    HeartIcon,
} from "@heroicons/react/24/outline";
import {
    ChatBubbleBottomCenterIcon as ChatBubbleBottomCenterSolidIcon,
    HandThumbUpIcon as HandThumbUpSolidIcon,
    PlusCircleIcon,
} from "@heroicons/react/24/solid";
import { useRef, useState } from "react";
import { EmojiFrownFill, EmojiSmileFill, Fire, Stars } from "react-bootstrap-icons";
import { Dropdown, DropdownContent, DropdownItem } from "components/elements";
import { cn, cva } from "lib/cn";
import { ClientReactionList, ClientReactionType } from "@compass/shared/types";

export const reactionDropdownClasses = cva(
    "rounded-none h-9 gap-2 text-sm font-medium text-slate-500 bg-slate-100",
    {
        variants: {
            type: {
                like: "hover:!bg-sky-500 hover:text-white",
                heart: "hover:!bg-rose-700 hover:text-white",
                celebrate: "hover:!bg-pink-500 hover:text-white",
                laugh: "hover:!bg-yellow-500 hover:text-white",
                sad: "hover:!bg-cyan-500 hover:text-white",
                angry: "hover:!bg-red-500 hover:text-white",
                unlike: "hover:!bg-sky-500 hover:text-white",
            },
        },
    },
);
export const reactionClasses = cva(
    "h-[26px] flex cursor-pointer items-center justify-center gap-1 rounded-full px-2 text-sm font-semibold text-slate-700",
    {
        variants: {
            type: {
                like: "text-sky-600",
                heart: "text-rose-700",
                celebrate: "text-pink-500",
                laugh: "text-yellow-500",
                sad: "text-cyan-600",
                angry: "text-red-500",
                react: "bg-slate-100 text-slate-500 shadow-none",
                unlike: "text-sky-600",
            },
            reactedType: {
                like: "bg-sky-600 text-white",
                heart: "bg-rose-700 text-white",
                celebrate: "bg-pink-500 text-white",
                laugh: "bg-yellow-500 text-white",
                sad: "bg-cyan-600 text-white",
                angry: "bg-red-500 text-white",
                unlike: "bg-sky-600 text-white",
            },
        },
    },
);

export const getReactionIcon = (type: ClientReactionType): React.FC<{ className?: string }> => {
    switch (type) {
        case "like":
            return HandThumbUpOutlineIcon;
        case "heart":
            return HeartIcon;
        case "celebrate":
            return Stars;
        case "laugh":
            return EmojiSmileFill;
        case "sad":
            return EmojiFrownFill;
        case "angry":
            return Fire;
        case "unlike":
            return HandThumbDownOutlineIcon;
        default:
            return HeartIcon;
    }
};

const REACTIONS: ClientReactionType[] = [
    "like",
    "heart",
    "angry",
    "celebrate",
    "laugh",
    "sad",
    "unlike",
];

export const ReactionDropdownContent = ({
    toggleReaction,
}: {
    toggleReaction: (type: ClientReactionType) => void;
}) => {
    return (
        <DropdownContent arrow={false} side="top" className="flex w-auto overflow-hidden p-0">
            {REACTIONS.map((type, key) => {
                const Icon = getReactionIcon(type);
                return (
                    <DropdownItem
                        key={key}
                        onSelect={() => toggleReaction(type)}
                        className={reactionDropdownClasses({ type })}
                    >
                        <Icon className={cn("h-5 w-5")} />
                    </DropdownItem>
                );
            })}
        </DropdownContent>
    );
};

export const ReactionsDropdown = ({
    toggleReaction,
    isPost,
}: {
    toggleReaction: (type: ClientReactionType) => void;
    isPost?: boolean;
}) => {
    const [open, setOpen] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    return (
        <div
            onMouseEnter={() => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                setOpen(true);
            }}
            onMouseLeave={() => {
                timeoutRef.current = setTimeout(() => setOpen(false), 120);
            }}
        >
            <Dropdown
                className="relative z-10"
                modal={false}
                open={open}
                onOpenChange={setOpen}
                content={<ReactionDropdownContent toggleReaction={toggleReaction} />}
            >
                <div className={reactionClasses({ type: "react" })}>
                    <PlusCircleIcon className="h-5 w-5 text-[#2da8e0]" />{" "}
                    <span className="font-normal text-[#424242]">{isPost ? "React" : ""}</span>
                </div>
            </Dropdown>
        </div>
    );
};

export const CommentsCount = ({ commentsCount }: { commentsCount?: number }) => {
    return (
        <div className="flex items-center justify-center gap-1 whitespace-nowrap text-sm text-slate-500">
            {/* <ChatBubbleBottomCenterSolidIcon className="h-5 w-5" />
            {pluralize(commentsCount || 0, "Comment", "Comments")} */}
        </div>
    );
};

type ReactionsProps = {
    toggleReaction: (type: ClientReactionType) => void;
    reactions: ClientReactionList;
    isCurrentUser: boolean;
    commentsCount?: number;
    isPost?: boolean;
};

export const CurrentReactions = ({
    reactions,
    isCurrentUser,
    toggleReaction,
}: {
    toggleReaction: (type: ClientReactionType) => void;
    reactions: ClientReactionList;
    isCurrentUser: boolean;
}) => {
    return (
        <>
            {REACTIONS.map((type) => {
                const Icon = getReactionIcon(type);

                const reaction = reactions[type];

                if (type !== "like" && (reaction?.count ?? 0) === 0) {
                    return null;
                }

                return (
                    <div
                        key={type}
                        className={reactionClasses(
                            isCurrentUser && reaction?.reacted
                                ? {
                                      reactedType: type,
                                  }
                                : { type },
                        )}
                        onClick={() => toggleReaction(type)}
                    >
                        <Icon
                            className={cn(
                                "h-4 w-4 text-[#09244a]",
                                reaction?.reacted && "drop-shadow",
                            )}
                        />
                        <span
                            className={cn(
                                "pr-[1px] pt-[1px] text-[#09244a]",
                                reaction?.reacted && "drop-shadow",
                            )}
                        >
                            {reactions[type]?.count ?? 0}
                        </span>
                    </div>
                );
            })}
        </>
    );
};

export const Reactions = ({
    reactions,
    commentsCount,
    toggleReaction,
    isCurrentUser,
    isPost,
}: ReactionsProps) => {
    return (
        <div
            className={`flex flex-row items-center justify-between gap-2 text-sm tracking-tight text-slate-500 ${
                isPost ? "" : ""
            }`}
        >
            <div className="flex flex-wrap items-center justify-start gap-1">
                {!isPost ? <div className="w-8" /> : null}

                <CurrentReactions
                    isCurrentUser={isCurrentUser}
                    reactions={reactions}
                    toggleReaction={toggleReaction}
                />

                <ReactionsDropdown isPost={isPost} toggleReaction={toggleReaction} />
            </div>

            {isPost ? <CommentsCount commentsCount={commentsCount} /> : null}
        </div>
    );
};
